import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';

const EditSalarySettings = ({
  show,
  onHide,
  settingId,
  employee,
  onSalarySettingUpdated
}) => {
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

  // Form state
  const [formData, setFormData] = useState({
    setting_id: '',
    emp_id: '',
    shift_id: '',
    policy_id: '',  // Added policy_id field
    template_id: '',
    leave_approve_by: '',
    weeklyoff_1: 'None',
    weeklyoff_2: 'None',
    salary_type: 'Monthly',
    salary_value: '',
    eligible_for_pf: 0,
    pf_percentage: '0.00',
    pf_employer_percentage: '0.00',
    pf_ceiling: '0.00',
    eligible_for_esi: 0,
    esi_percentage: '0.00',
    esi_employer_percentage: '0.00',
    esi_ceiling: '0.00',
    min_hour_for_present: '0.0',
    ot_hour_beyond_wh: '0.0',
    present_days_type: 'Standard',
    standard_days: '26',
    it_applicable: 'No',
    it_rule: '',
    // New fields
    permission_per_month: '0',
    permission_hour: '0',
    onduty_per_month: '0',
    auto_punch: 0,
    mobile_punch: 0,
    mobile_punch_option: 'Own',
    mass_mobile_punch: 0,
    // Location-based attendance fields
    allowed_lat: '',
    allowed_lng: '',
    allowed_radius: '200' // Default 200 meters
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [leaveApprovers, setLeaveApprovers] = useState([]);
  const [salaryTemplates, setSalaryTemplates] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [policies, setPolicies] = useState([]);  // Added policies state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [existingData, setExistingData] = useState(null);
  const [useLocationRestriction, setUseLocationRestriction] = useState(false);

  // Autocomplete states for leave approver
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredApprovers, setFilteredApprovers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingApprovers, setLoadingApprovers] = useState(false);
  const [, setSelectedApproverName] = useState('');

  // Weekly off options (matching database enum) with "None" option
  const weeklyOffOptions = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thusday', 'Friday', 'Saturday', 'None'];
  const salaryTypeOptions = ['Monthly', 'Daily', 'Hourly', 'Piece'];
  const workingDaysTypeOptions = ['Standard', 'SalaryMonth'];
  const itApplicableOptions = ['Yes', 'No'];

  // Initialize form with settingId
  useEffect(() => {
    if (settingId) {
      setFormData(prev => ({
        ...prev,
        setting_id: settingId
      }));
    }
  }, [settingId]);

  // Initialize form with employee data if provided
  useEffect(() => {
    if (employee) {
      setFormData(prev => ({
        ...prev,
        emp_id: employee.emp_id
      }));
    }
  }, [employee]);

  // Fetch leave approver name by ID
  const fetchApproverName = useCallback(async (approverId) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await axios.get(`${API_BASE}/get_employees.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        const approver = response.data.employees.find(
          emp => emp.emp_id === approverId
        );
        if (approver) {
          setSelectedApproverName(`${approver.first_name} ${approver.last_name} (${approver.emp_code})`);
          setSearchTerm(`${approver.first_name} ${approver.last_name} (${approver.emp_code})`);
        }
      }
    } catch (error) {
      console.error('Error fetching approver name:', error);
    }
  }, [API_BASE]);

  // Fetch leave approvers from get_employees.php
  const fetchLeaveApprovers = useCallback(async (search = '') => {
    try {
      setLoadingApprovers(true);
      const token = localStorage.getItem('authToken');

      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE}/get_employees.php`, {
        params: { search },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        // Filter out the current employee from approvers list
        const approvers = response.data.employees.filter(
          emp => emp.emp_id !== formData.emp_id
        );
        setLeaveApprovers(approvers);
        setFilteredApprovers(approvers);
      }
    } catch (error) {
      console.error('Error fetching leave approvers:', error);
    } finally {
      setLoadingApprovers(false);
    }
  }, [API_BASE, formData.emp_id]);

  // Fetch existing salary settings data
  useEffect(() => {
    const fetchExistingData = async () => {
      if (!settingId) return;

      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('authToken');

        if (!token) {
          setError('No authentication token found');
          return;
        }

        const response = await axios.get(`${API_BASE}/salarysettings/get_salary_settings.php`, {
          params: { setting_id: settingId },
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data.success && response.data.salary_settings) {
          const data = response.data.salary_settings;
          setExistingData(data);

          // Format the data for form
          setFormData({
            setting_id: data.setting_id,
            emp_id: data.emp_id,
            shift_id: data.shift_id || '',
            policy_id: data.policy_id || '',  // Include policy_id
            template_id: data.template_id || '',
            leave_approve_by: data.leave_approve_by || '',
            weeklyoff_1: data.weeklyoff_1 || 'None',
            weeklyoff_2: data.weeklyoff_2 || 'None',
            salary_type: data.salary_type || 'Monthly',
            salary_value: data.salary_value || '',
            eligible_for_pf: data.eligible_for_pf || 0,
            pf_percentage: data.pf_percentage || '0.00',
            pf_employer_percentage: data.pf_employer_percentage || '0.00',
            pf_ceiling: data.pf_ceiling || '0.00',
            eligible_for_esi: data.eligible_for_esi || 0,
            esi_percentage: data.esi_percentage || '0.00',
            esi_employer_percentage: data.esi_employer_percentage || '0.00',
            esi_ceiling: data.esi_ceiling || '0.00',
            min_hour_for_present: data.min_hour_for_present || '0.0',
            ot_hour_beyond_wh: data.ot_hour_beyond_wh || '0.0',
            present_days_type: data.present_days_type || 'Standard',
            standard_days: data.standard_days || '26',
            it_applicable: data.it_applicable || 'No',
            it_rule: data.it_rule || '',
            // New fields
            permission_per_month: data.permission_per_month || '0',
            permission_hour: data.permission_hour || '0',
            onduty_per_month: data.onduty_per_month || '0',
            auto_punch: Number(data.auto_punch) === 1 ? 1 : 0,
            mobile_punch: Number(data.mobile_punch) === 1 ? 1 : 0,
            mobile_punch_option: data.mobile_punch_option || 'Own',
            mass_mobile_punch: Number(data.mass_mobile_punch) === 1 ? 1 : 0,
            // Location fields
            allowed_lat: data.allowed_lat || '',
            allowed_lng: data.allowed_lng || '',
            allowed_radius: data.allowed_radius || '200'
          });

          // Set location restriction toggle if location fields exist
          if (data.allowed_lat && data.allowed_lng) {
            setUseLocationRestriction(true);
          } else {
            setUseLocationRestriction(false);
          }

          // If leave approver exists, find and set the name
          if (data.leave_approve_by) {
            // We'll fetch the approver details separately
            setTimeout(() => {
              fetchApproverName(data.leave_approve_by);
            }, 100);
          }
        } else {
          setError('Failed to load salary settings data');
        }
      } catch (error) {
        console.error('Error fetching salary settings:', error);
        setError('Failed to load salary settings. Please try again.');
      }
    };

    if (show && settingId) {
      fetchExistingData();
    }
  }, [show, settingId, API_BASE, fetchApproverName]);

  // Fetch salary templates, shifts, and policies
  useEffect(() => {
    const fetchData = async () => {
      try {
        setError('');
        const token = localStorage.getItem('authToken');

        if (!token) {
          setError('No authentication token found');
          return;
        }

        // Fetch salary templates
        const templatesResponse = await axios.get(`${API_BASE}/get_salary_templates.php`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        // Fetch shifts
        const shiftsResponse = await axios.get(`${API_BASE}/get_shifts.php`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        // Fetch attendance policies
        const policiesResponse = await axios.get(`${API_BASE}policy/get_policies.php`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        // Fetch leave approvers (initial load)
        await fetchLeaveApprovers('');

        if (templatesResponse.data.success) {
          setSalaryTemplates(templatesResponse.data.templates || []);
        }

        if (shiftsResponse.data.success) {
          setShifts(shiftsResponse.data.shifts || []);
        }

        if (policiesResponse.data.success) {
          setPolicies(policiesResponse.data.policies || []);
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load form data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (show) {
      fetchData();
      setSuccess('');
    }
  }, [show, API_BASE, fetchLeaveApprovers]);

  // Filter approvers based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredApprovers(leaveApprovers);
    } else {
      const filtered = leaveApprovers.filter(approver => {
        const fullName = `${approver.first_name} ${approver.last_name}`.toLowerCase();
        const empCode = approver.emp_code.toLowerCase();
        const search = searchTerm.toLowerCase();

        return fullName.includes(search) ||
          empCode.includes(search) ||
          approver.email.toLowerCase().includes(search);
      });
      setFilteredApprovers(filtered);
    }
  }, [searchTerm, leaveApprovers]);

  // Handle template selection
  useEffect(() => {
    if (formData.template_id && salaryTemplates.length > 0) {
      const template = salaryTemplates.find(t => t.template_id === formData.template_id);
      setSelectedTemplate(template);

      if (template) {
        // Apply template values to form, but only if not already set from existing data
        if (!existingData || existingData.template_id !== formData.template_id) {
          setFormData(prev => ({
            ...prev,
            salary_type: template.salary_type || 'Monthly',
            salary_value: template.base_salary || '',
            eligible_for_pf: template.eligible_for_pf || 0,
            pf_percentage: template.pf_percentage || '0.00',
            pf_employer_percentage: template.pf_employer_percentage || '0.00',
            pf_ceiling: template.pf_ceiling || '0.00',
            eligible_for_esi: template.eligible_for_esi || 0,
            esi_percentage: template.esi_percentage || '0.00',
            esi_employer_percentage: template.esi_employer_percentage || '0.00',
            esi_ceiling: template.esi_ceiling || '0.00',
            min_hour_for_present: template.min_hour_for_present || '0.0',
            ot_hour_beyond_wh: template.ot_hour_beyond_wh || '0.0',
            present_days_type: template.present_days_type || 'Standard',
            standard_days: template.standard_days || '26',
            it_applicable: template.it_applicable || 'No',
            it_rule: template.it_rule || '',
            // Apply new fields from template if available:
            permission_per_month: template.permission_per_month || '0',
            permission_hour: template.permission_hour || '0',
            onduty_per_month: template.onduty_per_month || '0',
            auto_punch: Number(template.auto_punch) === 1 ? 1 : 0,
            mobile_punch: Number(template.mobile_punch) === 1 ? 1 : 0,
            mobile_punch_option: template.mobile_punch_option || 'Own',
            mass_mobile_punch: Number(template.mass_mobile_punch) === 1 ? 1 : 0,
            // Location fields
            allowed_lat: template.allowed_lat || '',
            allowed_lng: template.allowed_lng || '',
            allowed_radius: template.allowed_radius || '200'
          }));
          // Set location restriction toggle if template has location fields
          if (template.allowed_lat && template.allowed_lng) {
            setUseLocationRestriction(true);
          } else {
            setUseLocationRestriction(false);
          }
        }
      }
    } else {
      setSelectedTemplate(null);
      // When template is cleared, reset template-related fields
      // Only reset if there is no existing data (i.e., not editing)
      if (!existingData) {
        setFormData(prev => ({
          ...prev,
          salary_type: 'Monthly',
          salary_value: '',
          // Reset location fields
          allowed_lat: '',
          allowed_lng: '',
          allowed_radius: '200'
        }));
        setUseLocationRestriction(false);
      }
    }
  }, [formData.template_id, salaryTemplates, existingData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked ? 1 : 0
      }));
    } else if (type === 'radio') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleTemplateSelect = (templateId) => {
    if (templateId === '') {
      // When template is cleared, reset template-related fields
      setFormData(prev => ({
        ...prev,
        template_id: '',
        salary_type: 'Monthly',
        salary_value: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        template_id: templateId
      }));
    }
  };

  // Handle location coordinates input
  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          allowed_lat: position.coords.latitude.toFixed(6),
          allowed_lng: position.coords.longitude.toFixed(6)
        }));
        setSuccess('Location captured successfully!');
        setTimeout(() => setSuccess(''), 3000);
      },
      (error) => {
        console.error('Error getting location:', error);
        let errorMessage = 'Unable to get your location. ';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Please enable location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out.';
            break;
          default:
            errorMessage += 'Please enter coordinates manually.';
        }
        setError(errorMessage);
        setTimeout(() => setError(''), 5000);
      }
    );
  };

  // Handle leave approver search input
  const handleApproverSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);

    // Debounce search API call
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      if (value.trim().length > 0) {
        fetchLeaveApprovers(value);
      }
    }, 300);
  };

  // Select an approver from dropdown
  const selectApprover = (approver) => {
    setFormData(prev => ({
      ...prev,
      leave_approve_by: approver.emp_id
    }));
    setSelectedApproverName(`${approver.first_name} ${approver.last_name} (${approver.emp_code})`);
    setSearchTerm(`${approver.first_name} ${approver.last_name} (${approver.emp_code})`);
    setShowDropdown(false);
  };

  // Clear selected approver
  const clearApprover = () => {
    setFormData(prev => ({
      ...prev,
      leave_approve_by: ''
    }));
    setSelectedApproverName('');
    setSearchTerm('');
    setShowDropdown(false);
  };

  const validateForm = () => {
    // Basic validation
    if (!formData.emp_id) {
      setError('Employee ID is required');
      return false;
    }

    if (!formData.salary_value || parseFloat(formData.salary_value) <= 0) {
      setError('Valid salary value is required');
      return false;
    }

    // Check if both weekly offs are the same (and not "None")
    if (formData.weeklyoff_1 === formData.weeklyoff_2 && formData.weeklyoff_2 !== 'None') {
      setError('Weekly off days cannot be the same');
      return false;
    }

    // If template is selected, validate that salary type and value are set
    if (formData.template_id && (!formData.salary_type || !formData.salary_value)) {
      setError('Salary type and value are required when using a template');
      return false;
    }

    // Validate PF percentages
    if (formData.eligible_for_pf) {
      const pfPerc = parseFloat(formData.pf_percentage);
      const pfEmpPerc = parseFloat(formData.pf_employer_percentage);
      if (isNaN(pfPerc) || pfPerc < 0 || pfPerc > 100 ||
        isNaN(pfEmpPerc) || pfEmpPerc < 0 || pfEmpPerc > 100) {
        setError('PF percentages must be between 0 and 100');
        return false;
      }
    }

    // Validate ESI percentages
    if (formData.eligible_for_esi) {
      const esiPerc = parseFloat(formData.esi_percentage);
      const esiEmpPerc = parseFloat(formData.esi_employer_percentage);
      if (isNaN(esiPerc) || esiPerc < 0 || esiPerc > 100 ||
        isNaN(esiEmpPerc) || esiEmpPerc < 0 || esiEmpPerc > 100) {
        setError('ESI percentages must be between 0 and 100');
        return false;
      }
    }

    // Validate standard days
    const stdDays = parseInt(formData.standard_days);
    if (isNaN(stdDays) || stdDays < 1 || stdDays > 31) {
      setError('Standard days must be between 1 and 31');
      return false;
    }

    // Validate new fields
    const permissionPerMonth = parseInt(formData.permission_per_month);
    if (isNaN(permissionPerMonth) || permissionPerMonth < 0 || permissionPerMonth > 31) {
      setError('Permission per month must be between 0 and 31');
      return false;
    }

    const permissionHour = parseInt(formData.permission_hour);
    if (isNaN(permissionHour) || permissionHour < 0 || permissionHour > 24) {
      setError('Permission hour must be between 0 and 24');
      return false;
    }

    const ondutyPerMonth = parseInt(formData.onduty_per_month);
    if (isNaN(ondutyPerMonth) || ondutyPerMonth < 0 || ondutyPerMonth > 31) {
      setError('On duty per month must be between 0 and 31');
      return false;
    }

    // Validate location fields if enabled
    if (useLocationRestriction) {
      if (!formData.allowed_lat || isNaN(parseFloat(formData.allowed_lat))) {
        setError('Valid latitude is required when location restriction is enabled');
        return false;
      }

      const lat = parseFloat(formData.allowed_lat);
      if (lat < -90 || lat > 90) {
        setError('Latitude must be between -90 and 90');
        return false;
      }

      if (!formData.allowed_lng || isNaN(parseFloat(formData.allowed_lng))) {
        setError('Valid longitude is required when location restriction is enabled');
        return false;
      }

      const lng = parseFloat(formData.allowed_lng);
      if (lng < -180 || lng > 180) {
        setError('Longitude must be between -180 and 180');
        return false;
      }

      const radius = parseInt(formData.allowed_radius);
      if (isNaN(radius) || radius < 0 || radius > 5000) {
        setError('Allowed radius must be between 0 and 5000 meters');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');

      // Prepare the data for API
      const submitData = {
        setting_id: formData.setting_id,
        emp_id: formData.emp_id,
        shift_id: formData.shift_id ? parseInt(formData.shift_id) : null,
        policy_id: formData.policy_id ? parseInt(formData.policy_id) : null,  // Include policy_id
        template_id: formData.template_id ? parseInt(formData.template_id) : null,
        leave_approve_by: formData.leave_approve_by ? parseInt(formData.leave_approve_by) : 0,
        weeklyoff_1: formData.weeklyoff_1,
        weeklyoff_2: formData.weeklyoff_2,
        salary_type: formData.salary_type,
        salary_value: parseFloat(formData.salary_value),
        eligible_for_pf: formData.eligible_for_pf ? 1 : 0,
        pf_percentage: parseFloat(formData.pf_percentage),
        pf_employer_percentage: parseFloat(formData.pf_employer_percentage),
        pf_ceiling: parseFloat(formData.pf_ceiling),
        eligible_for_esi: formData.eligible_for_esi ? 1 : 0,
        esi_percentage: parseFloat(formData.esi_percentage),
        esi_employer_percentage: parseFloat(formData.esi_employer_percentage),
        esi_ceiling: parseFloat(formData.esi_ceiling),
        min_hour_for_present: parseFloat(formData.min_hour_for_present),
        ot_hour_beyond_wh: parseFloat(formData.ot_hour_beyond_wh),
        present_days_type: formData.present_days_type,
        standard_days: parseInt(formData.standard_days),
        it_applicable: formData.it_applicable,
        it_rule: formData.it_rule,
        // New fields
        permission_per_month: parseInt(formData.permission_per_month),
        permission_hour: parseInt(formData.permission_hour),
        onduty_per_month: parseInt(formData.onduty_per_month),
        auto_punch: formData.auto_punch ? 1 : 0,
        mobile_punch: formData.mobile_punch ? 1 : 0,
        mobile_punch_option: formData.mobile_punch_option,
        mass_mobile_punch: formData.mass_mobile_punch ? 1 : 0,
        // Location fields - only include if location restriction is enabled
        allowed_lat: useLocationRestriction && formData.allowed_lat ? parseFloat(formData.allowed_lat) : null,
        allowed_lng: useLocationRestriction && formData.allowed_lng ? parseFloat(formData.allowed_lng) : null,
        allowed_radius: useLocationRestriction && formData.allowed_radius ? parseInt(formData.allowed_radius) : 200
      };

      const response = await axios.post(
        `${API_BASE}/salarysettings/update_salary_settings.php`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess('Salary settings updated successfully!');
        setTimeout(() => {
          onSalarySettingUpdated();
          handleClose();
        }, 1500);
      } else {
        setError(response.data.error || 'Failed to update salary settings');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error ||
        error.message ||
        'Failed to update salary settings';
      setError(errorMsg);
      console.error('API Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      setting_id: '',
      emp_id: employee?.emp_id || '',
      shift_id: '',
      policy_id: '',  // Reset policy_id
      template_id: '',
      leave_approve_by: '',
      weeklyoff_1: 'None',
      weeklyoff_2: 'None',
      salary_type: 'Monthly',
      salary_value: '',
      eligible_for_pf: 0,
      pf_percentage: '0.00',
      pf_employer_percentage: '0.00',
      pf_ceiling: '0.00',
      eligible_for_esi: 0,
      esi_percentage: '0.00',
      esi_employer_percentage: '0.00',
      esi_ceiling: '0.00',
      min_hour_for_present: '0.0',
      ot_hour_beyond_wh: '0.0',
      present_days_type: 'Standard',
      standard_days: '26',
      it_applicable: 'No',
      it_rule: '',
      // Reset new fields
      permission_per_month: '0',
      permission_hour: '0',
      onduty_per_month: '0',
      auto_punch: 0,
      mobile_punch: 0,
      mobile_punch_option: 'Own',
      mass_mobile_punch: 0,
      // Reset location fields
      allowed_lat: '',
      allowed_lng: '',
      allowed_radius: '200'
    });
    setSelectedTemplate(null);
    setExistingData(null);
    setUseLocationRestriction(false);
    setError('');
    setSuccess('');
    setSearchTerm('');
    setSelectedApproverName('');
    setFilteredApprovers([]);
    setShowDropdown(false);
    setSalaryTemplates([]);
    setShifts([]);
    setPolicies([]);  // Reset policies
    onHide();
  };

  // Get employee display name
  const getEmployeeName = () => {
    if (existingData) {
      return `${existingData.first_name} ${existingData.last_name}`;
    }
    if (employee) {
      return `${employee.first_name} ${employee.last_name}`;
    }
    return 'Employee';
  };

  // Get employee code
  const getEmployeeCode = () => {
    if (existingData) {
      return existingData.emp_code;
    }
    if (employee) {
      return employee.emp_code;
    }
    return 'N/A';
  };

  // Format shift display name
  const formatShiftName = (shift) => {
    if (!shift) return '';
    const startTime = shift.start_time ? shift.start_time.substring(0, 5) : '';
    const endTime = shift.end_time ? shift.end_time.substring(0, 5) : '';
    return `${shift.shift_name} (${startTime} - ${endTime})`;
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      backdrop="static"
      centered
      scrollable
      animation={false}
    >
      <Modal.Header closeButton className="bg-warning text-white">
        <Modal.Title>
          <i className="bi bi-pencil-square me-2"></i>
          Edit Salary Settings for {getEmployeeName()}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <Form id="edit-salary-settings-form" onSubmit={handleSubmit}>
          {/* Loading State */}
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="warning" />
              <p className="mt-3 text-muted">Loading salary settings data...</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <Alert variant="success" className="d-flex align-items-center">
              <i className="bi bi-check-circle-fill me-2"></i>
              {success}
            </Alert>
          )}

          {/* Error Message */}
          {error && !loading && (
            <Alert variant="danger" className="d-flex align-items-center">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </Alert>
          )}

          {/* Form Content */}
          {!loading && (
            <>
              {/* Employee Info Card */}
              <div className="card mb-4 border-warning">
                <div className="card-header bg-light">
                  <h5 className="mb-0">
                    <i className="bi bi-person-badge me-2 text-warning"></i>
                    Employee Information
                  </h5>
                </div>
                <div className="card-body">
                  <Row>
                    <Col md={6}>
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-warning rounded-circle p-2 me-3">
                          <i className="bi bi-person text-white"></i>
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">
                            {getEmployeeName()}
                          </h6>
                          <small className="text-muted">
                            ID: {formData.emp_id} | Code: {getEmployeeCode()}
                          </small>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="bg-light p-3 rounded">
                        <small className="text-muted d-block">Setting ID</small>
                        <span className="fw-semibold">{formData.setting_id || 'N/A'}</span>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>

              <Row>
                {/* Left Column - Basic Settings */}
                <Col lg={6}>
                  <div className="card mb-4">
                    <div className="card-header bg-light">
                      <h6 className="mb-0">
                        <i className="bi bi-sliders me-2"></i>
                        Basic Settings
                      </h6>
                    </div>
                    <div className="card-body">
                      {/* Shift Selection */}
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <i className="bi bi-clock me-1 text-primary"></i>
                          Shift (Optional)
                        </Form.Label>
                        <Form.Select
                          name="shift_id"
                          value={formData.shift_id}
                          onChange={handleInputChange}
                          className="form-select"
                        >
                          <option value="">Select Shift</option>
                          {shifts.length > 0 ? (
                            shifts.map(shift => (
                              <option key={shift.shift_id} value={shift.shift_id}>
                                {formatShiftName(shift)}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>No shifts available</option>
                          )}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Select the shift for this employee (optional)
                        </Form.Text>
                      </Form.Group>

                      {/* Attendance Policy Selection */}
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <i className="bi bi-clipboard-check me-1 text-primary"></i>
                          Attendance Policy (Optional)
                        </Form.Label>
                        <Form.Select
                          name="policy_id"
                          value={formData.policy_id}
                          onChange={handleInputChange}
                          className="form-select"
                        >
                          <option value="">Select Attendance Policy</option>
                          {policies.length > 0 ? (
                            policies.map(policy => (
                              <option key={policy.policy_id} value={policy.policy_id}>
                                {policy.policy_name}
                                {policy.attendance_mode ? ` (${policy.attendance_mode})` : ''}
                                {!policy.is_active && ' - Inactive'}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>No policies available</option>
                          )}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Select an attendance policy for this employee (optional)
                        </Form.Text>
                      </Form.Group>

                      {/* Salary Template */}
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <i className="bi bi-file-earmark-text me-1 text-primary"></i>
                          Salary Template (Optional)
                        </Form.Label>
                        <Form.Select
                          name="template_id"
                          value={formData.template_id}
                          onChange={(e) => handleTemplateSelect(e.target.value)}
                          className="form-select"
                        >
                          <option value="">Select Template</option>
                          {salaryTemplates.length > 0 ? (
                            salaryTemplates.map(template => (
                              <option key={template.template_id} value={template.template_id}>
                                {template.template_name}
                                {template.component_count ? ` (${template.component_count} components)` : ''}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>No templates available</option>
                          )}
                        </Form.Select>
                        {selectedTemplate && (
                          <div className="mt-2 p-2 bg-info bg-opacity-10 border border-info rounded">
                            <small className="text-info">
                              <i className="bi bi-info-circle me-1"></i>
                              Template "{selectedTemplate.template_name}" values have been loaded. You can still adjust the salary type and value as needed.
                            </small>
                          </div>
                        )}
                        <Form.Text className="text-muted">
                          Select a template to pre-fill salary settings
                        </Form.Text>
                      </Form.Group>

                      {/* Salary Type and Value */}
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Salary Type *</Form.Label>
                            <Form.Select
                              name="salary_type"
                              value={formData.salary_type}
                              onChange={handleInputChange}
                              required
                            >
                              {salaryTypeOptions.map(type => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              <i className="bi bi-currency-rupee me-1 text-success"></i>
                              Salary Value *
                            </Form.Label>
                            <Form.Control
                              type="number"
                              name="salary_value"
                              value={formData.salary_value}
                              onChange={handleInputChange}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              required
                              className="form-control"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* Weekly Off Days */}
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Weekly Off Day 1 (Optional)</Form.Label>
                            <Form.Select
                              name="weeklyoff_1"
                              value={formData.weeklyoff_1}
                              onChange={handleInputChange}
                            >
                              {weeklyOffOptions.map(day => (
                                <option key={day} value={day}>{day}</option>
                              ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                              Select "None" if employee has no weekly off
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Weekly Off Day 2 (Optional)</Form.Label>
                            <Form.Select
                              name="weeklyoff_2"
                              value={formData.weeklyoff_2}
                              onChange={handleInputChange}
                            >
                              {weeklyOffOptions.map(day => (
                                <option key={day} value={day}>{day}</option>
                              ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                              Select "None" if employee has only one weekly off
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* Leave Approver - Autocomplete */}
                      <Form.Group className="mb-3 position-relative">
                        <Form.Label>
                          <i className="bi bi-person-check me-1 text-primary"></i>
                          Leave Approver (Optional)
                        </Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type="text"
                            placeholder="Search employee by name or code..."
                            value={searchTerm}
                            onChange={handleApproverSearch}
                            onFocus={() => setShowDropdown(true)}
                            autoComplete="off"
                          />
                          {formData.leave_approve_by && (
                            <Button
                              variant="outline-secondary"
                              onClick={clearApprover}
                              title="Clear selection"
                            >
                              <i className="bi bi-x"></i>
                            </Button>
                          )}
                        </div>

                        {/* Autocomplete Dropdown */}
                        {showDropdown && (
                          <div
                            className="position-absolute w-100 bg-white border rounded shadow-lg mt-1"
                            style={{ zIndex: 1050, maxHeight: '300px', overflowY: 'auto' }}
                          >
                            {loadingApprovers ? (
                              <div className="text-center p-3">
                                <Spinner animation="border" size="sm" />
                                <span className="ms-2">Searching...</span>
                              </div>
                            ) : filteredApprovers.length > 0 ? (
                              filteredApprovers.map(approver => (
                                <div
                                  key={approver.emp_id}
                                  className={`dropdown-item p-3 cursor-pointer ${formData.leave_approve_by === approver.emp_id
                                    ? 'bg-warning text-white'
                                    : 'hover-bg-light'
                                    }`}
                                  onClick={() => selectApprover(approver)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <strong>{approver.first_name} {approver.last_name}</strong>
                                      <div className="small text-muted">
                                        Code: {approver.emp_code} | Email: {approver.email}
                                      </div>
                                    </div>
                                    {approver.status === 'Inactive' && (
                                      <span className="badge bg-warning">Inactive</span>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-center p-3 text-muted">
                                No employees found. Try a different search.
                              </div>
                            )}
                          </div>
                        )}

                        <Form.Text className="text-muted">
                          Search and select an employee who can approve leaves for this employee
                        </Form.Text>
                      </Form.Group>
                    </div>
                  </div>

                  {/* Working Days Settings */}
                  <div className="card mb-4">
                    <div className="card-header bg-light">
                      <h6 className="mb-0">
                        <i className="bi bi-calendar-week me-2"></i>
                        Working Days Settings
                      </h6>
                    </div>
                    <div className="card-body">
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Working Days Type</Form.Label>
                            <Form.Select
                              name="present_days_type"
                              value={formData.present_days_type}
                              onChange={handleInputChange}
                            >
                              {workingDaysTypeOptions.map(type => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Standard Days</Form.Label>
                            <Form.Control
                              type="number"
                              name="standard_days"
                              value={formData.standard_days}
                              onChange={handleInputChange}
                              min="1"
                              max="31"
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Min Hours for Present</Form.Label>
                            <Form.Control
                              type="number"
                              name="min_hour_for_present"
                              value={formData.min_hour_for_present}
                              onChange={handleInputChange}
                              step="0.1"
                              min="0"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>OT Hours Beyond WH</Form.Label>
                            <Form.Control
                              type="number"
                              name="ot_hour_beyond_wh"
                              value={formData.ot_hour_beyond_wh}
                              onChange={handleInputChange}
                              step="0.1"
                              min="0"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* New Fields: Permission and On Duty Settings */}
                      <Row>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              <i className="bi bi-fingerprint me-1"></i>
                              Allowed Auto Punch
                            </Form.Label>
                            <Form.Check
                              type="switch"
                              id="edit-auto-punch-switch"
                              name="auto_punch"
                              checked={!!formData.auto_punch}
                              onChange={handleInputChange}
                              label={formData.auto_punch ? "Enabled" : "Disabled"}
                            />
                            <Form.Text className="text-muted">
                              Allow auto punch for this employee
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              <i className="bi bi-phone me-1"></i>
                              Mobile Punch
                            </Form.Label>
                            <Form.Check
                              type="switch"
                              id="edit-mobile-punch-switch"
                              name="mobile_punch"
                              checked={!!formData.mobile_punch}
                              onChange={handleInputChange}
                              label={formData.mobile_punch ? "Enabled" : "Disabled"}
                            />
                            <Form.Text className="text-muted">
                              Allow mobile punch for this employee
                            </Form.Text>
                          </Form.Group>
                          {!!formData.mobile_punch && (
                            <Form.Group className="mb-3 border-start border-3 border-primary ps-2">
                              <Form.Select
                                name="mobile_punch_option"
                                value={formData.mobile_punch_option}
                                onChange={handleInputChange}
                                size="sm"
                                className="mb-2"
                              >
                                <option value="Own">Own Device</option>
                                <option value="Common">Common Device</option>
                              </Form.Select>
                              {formData.mobile_punch_option === 'Common' && (
                                <Form.Check
                                  type="switch"
                                  id="edit-mass-mobile-punch-switch"
                                  name="mass_mobile_punch"
                                  checked={!!formData.mass_mobile_punch}
                                  onChange={handleInputChange}
                                  label="Mass Punch"
                                />
                              )}
                            </Form.Group>
                          )}
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              <i className="bi bi-person-check me-1"></i>
                              Permission Per Month
                            </Form.Label>
                            <Form.Control
                              type="number"
                              name="permission_per_month"
                              value={formData.permission_per_month}
                              onChange={handleInputChange}
                              min="0"
                              max="31"
                              placeholder="0"
                            />
                            <Form.Text className="text-muted">
                              Max permissions allowed per month
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              <i className="bi bi-clock me-1"></i>
                              Permission Hour Limit
                            </Form.Label>
                            <Form.Control
                              type="number"
                              name="permission_hour"
                              value={formData.permission_hour}
                              onChange={handleInputChange}
                              min="0"
                              max="24"
                              placeholder="0"
                            />
                            <Form.Text className="text-muted">
                              Max hours per permission
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              <i className="bi bi-briefcase me-1"></i>
                              On Duty Per Month
                            </Form.Label>
                            <Form.Control
                              type="number"
                              name="onduty_per_month"
                              value={formData.onduty_per_month}
                              onChange={handleInputChange}
                              min="0"
                              max="31"
                              placeholder="0"
                            />
                            <Form.Text className="text-muted">
                              Max on-duty days per month
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>

                {/* Right Column - Deductions & Tax */}
                <Col lg={6}>
                  {/* Deductions & Eligibility Settings */}
                  <div className="card mb-4">
                    <div className="card-header bg-light">
                      <h6 className="mb-0">
                        <i className="bi bi-shield-check me-2"></i>
                        Deductions & Eligibility Settings
                      </h6>
                    </div>
                    <div className="card-body">
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-0">
                            <Form.Label className="fw-semibold mb-2">PF Contribution</Form.Label>
                            <Form.Check
                              type="switch"
                              id="pf-switch"
                              name="eligible_for_pf"
                              checked={!!formData.eligible_for_pf}
                              onChange={handleInputChange}
                              label={formData.eligible_for_pf ? "Eligible for PF" : "Not Eligible for PF"}
                              className="fw-medium text-primary"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-0">
                            <Form.Label className="fw-semibold mb-2">ESI Contribution</Form.Label>
                            <Form.Check
                              type="switch"
                              id="esi-switch"
                              name="eligible_for_esi"
                              checked={!!formData.eligible_for_esi}
                              onChange={handleInputChange}
                              label={formData.eligible_for_esi ? "Eligible for ESI" : "Not Eligible for ESI"}
                              className="fw-medium text-danger"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  </div>

                  {/* Location Restriction Settings */}
                  <div className="card mb-4">
                    <div className="card-header bg-light">
                      <h6 className="mb-0">
                        <i className="bi bi-geo-alt me-2"></i>
                        Location Restriction for Attendance
                      </h6>
                    </div>
                    <div className="card-body">
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          id="edit-location-restriction-switch"
                          label="Enable Location Restriction"
                          checked={useLocationRestriction}
                          onChange={(e) => setUseLocationRestriction(e.target.checked)}
                        />
                        <Form.Text className="text-muted">
                          Restrict attendance marking to a specific location
                        </Form.Text>
                      </Form.Group>

                      {useLocationRestriction && (
                        <>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Allowed Latitude *</Form.Label>
                                <Form.Control
                                  type="number"
                                  name="allowed_lat"
                                  value={formData.allowed_lat}
                                  onChange={handleLocationChange}
                                  placeholder="e.g., 28.6139"
                                  step="any"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Allowed Longitude *</Form.Label>
                                <Form.Control
                                  type="number"
                                  name="allowed_lng"
                                  value={formData.allowed_lng}
                                  onChange={handleLocationChange}
                                  placeholder="e.g., 77.2090"
                                  step="any"
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Form.Group className="mb-3">
                            <Form.Label>Allowed Radius (meters)</Form.Label>
                            <Form.Control
                              type="number"
                              name="allowed_radius"
                              value={formData.allowed_radius}
                              onChange={handleInputChange}
                              min="0"
                              max="5000"
                              step="50"
                            />
                            <Form.Text className="text-muted">
                              Maximum distance from the allowed location (0-5000 meters)
                            </Form.Text>
                          </Form.Group>

                          <Button
                            variant="outline-primary"
                            onClick={getCurrentLocation}
                            className="mt-2"
                            type="button"
                          >
                            <i className="bi bi-crosshair me-2"></i>
                            Get Current Location
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Income Tax Settings */}
                  <div className="card">
                    <div className="card-header bg-light">
                      <h6 className="mb-0">
                        <i className="bi bi-calculator me-2"></i>
                        Income Tax Settings
                      </h6>
                    </div>
                    <div className="card-body">
                      <Row className="mb-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>IT Applicable</Form.Label>
                            <div>
                              {itApplicableOptions.map(option => (
                                <Form.Check
                                  key={option}
                                  type="radio"
                                  name="it_applicable"
                                  id={`it-${option}`}
                                  label={option}
                                  value={option}
                                  checked={formData.it_applicable === option}
                                  onChange={handleInputChange}
                                  inline
                                />
                              ))}
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>

                      {formData.it_applicable === 'Yes' && (
                        <Form.Group>
                          <Form.Label>IT Rule</Form.Label>
                          <Form.Control
                            as="textarea"
                            name="it_rule"
                            value={formData.it_rule}
                            onChange={handleInputChange}
                            rows={3}
                            placeholder="Enter income tax rules or conditions..."
                          />
                          <Form.Text className="text-muted">
                            Enter specific tax rules, slab details, or conditions for this employee.
                          </Form.Text>
                        </Form.Group>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer className="bg-light">
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={submitting || loading}
        >
          <i className="bi bi-x-circle me-2"></i>
          Cancel
        </Button>
        <Button
          variant="warning"
          type="submit"
          form="edit-salary-settings-form"
          disabled={submitting || loading}
        >
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Updating...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle me-2"></i>
              Update Salary Settings
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditSalarySettings;